import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from './hooks/useAuth';

// Import pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';
import ChangePassword from './pages/ChangePassword';
import { TestEditorStandalone } from './pages/TestEditorStandalone';

// Import components
import ProtectedRoute from './components/shared/ProtectedRoute';

// Import layouts
import AdminLayout from './components/layouts/AdminLayout';
import SchoolAdminLayout from './components/layouts/SchoolAdminLayout';
import TeacherLayout from './components/layouts/TeacherLayout';
import StudentLayout from './components/layouts/StudentLayout';

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Home />} />
        <Route
          path="/login"
          element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />}
        />
        <Route
          path="/register"
          element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" />}
        />

        {/* Unauthorized access page */}
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Change password page (for FORCE_CHANGE_PASSWORD users) */}
        <Route path="/change-password" element={<ChangePassword />} />

        {/* Unauthenticated throwaway editor test page */}
        <Route path="/editor-test" element={<TestEditorStandalone />} />

        {/* Protected routes - Basic authentication required */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Role-based protected routes */}
        {/* Admin-only routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        />

        {/* School Admin routes (accessible by admin and school_admin) */}
        <Route
          path="/school-admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin', 'school_admin']}>
              <SchoolAdminLayout />
            </ProtectedRoute>
          }
        />

        {/* Teacher routes (accessible by admin, school_admin, and teacher) */}
        <Route
          path="/teacher/*"
          element={
            <ProtectedRoute allowedRoles={['admin', 'school_admin', 'teacher']}>
              <TeacherLayout />
            </ProtectedRoute>
          }
        />

        {/* Student routes (student-only) */}
        <Route
          path="/student/*"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentLayout />
            </ProtectedRoute>
          }
        />

        {/* 404 route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Box>
  );
};

export default App;
